import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { getDemoProfile, saveDemoProfile, clearDemoProfile, type DemoProfile } from "@/lib/demo-profile";
import { mergeEffectiveFeatures, type PlanFeatures } from "@/lib/plans";

const API_BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

export interface ApiPlanConfig {
  planId: string;
  label: string;
  features: Record<string, boolean> | null;
}

interface DemoProfileContextType {
  profile: DemoProfile | null;
  planConfigs: ApiPlanConfig[];
  saveProfile: (p: DemoProfile) => void;
  setPlanId: (id: string, companyId?: string) => void;
  clearProfile: () => void;
  getEffectiveFeatures: (planId: string) => PlanFeatures;
}

const DemoProfileContext = createContext<DemoProfileContextType>({
  profile: null,
  planConfigs: [],
  saveProfile: () => {},
  setPlanId: () => {},
  clearProfile: () => {},
  getEffectiveFeatures: (planId) => mergeEffectiveFeatures(planId, null, null),
});

export function DemoProfileProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<DemoProfile | null>(() => getDemoProfile());

  const { data: planConfigs = [] } = useQuery<ApiPlanConfig[]>({
    queryKey: ["public-plan-configs"],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/api/plans`);
      if (!res.ok) return [];
      return res.json();
    },
    staleTime: 1000 * 60 * 5,
  });

  const getEffectiveFeatures = useCallback(
    (planId: string): PlanFeatures => {
      const cpConfig = planConfigs.find(c => c.planId === planId);
      return mergeEffectiveFeatures(planId, cpConfig?.features ?? null, null);
    },
    [planConfigs],
  );

  const saveProfile = (p: DemoProfile) => {
    saveDemoProfile(p);
    setProfile(p);
  };

  const setPlanId = (planId: string, companyId?: string) => {
    setProfile(prev => {
      const updated: DemoProfile = { ...(prev ?? {}), planId };
      if (companyId !== undefined) updated.companyId = companyId;
      saveDemoProfile(updated);
      return updated;
    });
  };

  const clearProfile = () => {
    clearDemoProfile();
    setProfile(null);
  };

  return (
    <DemoProfileContext.Provider value={{ profile, planConfigs, saveProfile, setPlanId, clearProfile, getEffectiveFeatures }}>
      {children}
    </DemoProfileContext.Provider>
  );
}

export function useDemoProfile() {
  return useContext(DemoProfileContext);
}
